import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { UserCircle, Mail, Building2, IdCard, Settings } from 'lucide-react';
import SecurityCard from '../components/profile/SecurityCard';
import ChangePasswordCard from '../components/profile/ChangePasswordCard';
import DemoDataCard from '../components/profile/DemoDataCard';
import NotificationPreferencesCard from '../components/profile/NotificationPreferencesCard';

const ROLE_LABELS = {
  admin: 'Administrator',
  fleet_manager: 'Fleet Manager',
  mechanic: 'Mechanic',
};

export const UserProfilePage = () => {
  const { user, isAdmin } = useAuth();
  const navigate = useNavigate();

  const initial = (user?.name || user?.email || '?').charAt(0).toUpperCase();

  return (
    <div className="space-y-8 animate-fade-in max-w-4xl" data-testid="user-profile-page">
      {/* Identity header */}
      <div className="flex flex-col sm:flex-row sm:items-center gap-5">
        <div className="w-16 h-16 rounded-2xl bg-primary flex items-center justify-center shrink-0 shadow-sm">
          <span className="text-primary-foreground font-extrabold text-2xl">{initial}</span>
        </div>
        <div className="min-w-0">
          <h1 className="text-[22px] font-extrabold tracking-tight truncate" style={{ fontFamily: 'Manrope, sans-serif' }} data-testid="profile-name">
            {user?.name || 'Your profile'}
          </h1>
          <div className="flex items-center gap-2 mt-1 flex-wrap">
            <Badge variant="outline" className="uppercase text-[10px] tracking-wider" data-testid="profile-role">
              {ROLE_LABELS[user?.role] || user?.role || 'User'}
            </Badge>
            {user?.company_name && (
              <span className="text-sm text-muted-foreground truncate">{user.company_name}</span>
            )}
          </div>
        </div>
        {isAdmin() && (
          <Button
            variant="outline"
            className="sm:ml-auto shrink-0"
            onClick={() => navigate('/settings')}
            data-testid="profile-open-settings-btn"
          >
            <Settings className="w-4 h-4 mr-2" />
            Workspace settings
          </Button>
        )}
      </div>

      {/* Account details */}
      <Card data-testid="account-details-card">
        <CardHeader>
          <CardTitle className="uppercase tracking-wide flex items-center gap-2">
            <UserCircle className="w-5 h-5" />
            Account details
          </CardTitle>
          <CardDescription>Your personal sign-in identity for this workspace.</CardDescription>
        </CardHeader>
        <CardContent className="grid sm:grid-cols-2 gap-4">
          <Field icon={UserCircle} label="Full name" value={user?.name} testid="profile-field-name" />
          <Field icon={Mail} label="Email" value={user?.email} testid="profile-field-email" />
          <Field icon={Building2} label="Company" value={user?.company_name || '—'} testid="profile-field-company" />
          {user?.client_id && (
            <Field icon={IdCard} label="Client ID" value={user.client_id} mono testid="profile-field-client" />
          )}
        </CardContent>
      </Card>

      <DemoDataCard />
      <NotificationPreferencesCard />
      <SecurityCard />
      <ChangePasswordCard />
    </div>
  );
};

const Field = ({ icon: Icon, label, value, mono, testid }) => (
  <div className="rounded-lg border border-border bg-card/40 px-4 py-3" data-testid={testid}>
    <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.2em] text-muted-foreground font-semibold mb-1">
      <Icon className="w-3.5 h-3.5" />
      {label}
    </div>
    <p className={`text-sm font-medium break-words ${mono ? 'font-mono' : ''}`}>{value || '—'}</p>
  </div>
);

export default UserProfilePage;
